import {createEstablishedAhpSessionBootstrap} from '@github-ui/ahp-session-client/bootstrap'
import type {
  EstablishedAhpDirectTurnTelemetry,
  EstablishedAhpPlanningPolicy,
  EstablishedAhpSessionBootstrap,
  EstablishedAhpSessionIdentity,
  EstablishedAhpSessionProvisioned,
  EstablishedAhpSessionRelayDiagnostics,
  EstablishRequest,
  ProvisionRequest,
} from '@github-ui/ahp-session-client/contracts'
import type {AcquireRelayLeaseOptions} from '@github-ui/ahp-relay/relay-registry'

export interface MissionControlAhpSessionRef {
  taskId: string
  environmentId: string
  sessionId: string
  channel?: string
}

type MissionControlRelayOptions = Omit<AcquireRelayLeaseOptions, 'environmentId' | 'sessionId' | 'channel' | 'taskId'> &
  Partial<Pick<AcquireRelayLeaseOptions, 'channel' | 'taskId'>>
type MissionControlAdapterRequest = Exclude<EstablishRequest, {mode: 'resume-provision'}>

export interface MissionControlEstablishedAhpBootstrapOptions {
  provisionSession(input: ProvisionRequest): Promise<MissionControlAhpSessionRef>
  createRelayOptions(
    identity: EstablishedAhpSessionIdentity,
    request?: MissionControlAdapterRequest,
  ): MissionControlRelayOptions
  attachSession?(identity: EstablishedAhpSessionIdentity): Promise<MissionControlAhpSessionRef>
  wakeSession?(identity: EstablishedAhpSessionIdentity): Promise<MissionControlAhpSessionRef>
  recoverSession?(identity: EstablishedAhpSessionIdentity): Promise<MissionControlAhpSessionRef>
  planning?: EstablishedAhpPlanningPolicy
  directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
}

function identityFor(session: MissionControlAhpSessionRef): EstablishedAhpSessionIdentity {
  return {
    taskId: session.taskId,
    environmentId: session.environmentId,
    sessionId: session.sessionId,
    ...(session.channel ? {channel: session.channel} : {}),
  }
}

function sessionForIdentity(identity: EstablishedAhpSessionIdentity): MissionControlAhpSessionRef {
  return {
    taskId: identity.taskId ?? identity.sessionId,
    environmentId: identity.environmentId,
    sessionId: identity.sessionId,
    ...(identity.channel ? {channel: identity.channel} : {}),
  }
}

function provisioned(
  session: MissionControlAhpSessionRef,
  createRelayOptions: MissionControlEstablishedAhpBootstrapOptions['createRelayOptions'],
  request: MissionControlAdapterRequest,
  relayDiagnostics: EstablishedAhpSessionRelayDiagnostics,
  directTurnTelemetry: EstablishedAhpDirectTurnTelemetry | undefined,
): EstablishedAhpSessionProvisioned {
  const identity = identityFor(session)
  return {
    identity,
    relay: {
      ...createRelayOptions(identity, request),
      ...relayDiagnostics,
      environmentId: identity.environmentId,
      sessionId: identity.sessionId,
      ...(identity.channel ? {channel: identity.channel} : {}),
      ...(identity.taskId ? {taskId: identity.taskId} : {}),
    },
    ...(directTurnTelemetry ? {directTurnTelemetry} : {}),
  }
}

/**
 * Mission Control owns task eligibility, history, wakeability, and token
 * wiring. Once it resolves a live AHP identity, this constructor hands it to
 * the same established-session bootstrap used by Quick Chat.
 */
export function createMissionControlEstablishedAhpBootstrap(
  options: MissionControlEstablishedAhpBootstrapOptions,
): EstablishedAhpSessionBootstrap {
  let relayDiagnostics: EstablishedAhpSessionRelayDiagnostics = {}
  const sharedBootstrap = createEstablishedAhpSessionBootstrap(
    {
      provision: async input =>
        provisioned(
          await options.provisionSession(input),
          options.createRelayOptions,
          {mode: 'provision', input},
          relayDiagnostics,
          options.directTurnTelemetry,
        ),
      attach: async identity =>
        provisioned(
          await (options.attachSession?.(identity) ?? Promise.resolve(sessionForIdentity(identity))),
          options.createRelayOptions,
          {mode: 'attach', identity},
          relayDiagnostics,
          options.directTurnTelemetry,
        ),
      wake: async identity =>
        provisioned(
          await (options.wakeSession?.(identity) ?? Promise.resolve(sessionForIdentity(identity))),
          options.createRelayOptions,
          {mode: 'wake', identity},
          relayDiagnostics,
          options.directTurnTelemetry,
        ),
      recover: async identity =>
        provisioned(
          await (options.recoverSession?.(identity) ?? Promise.resolve(sessionForIdentity(identity))),
          options.createRelayOptions,
          {mode: 'recover', identity},
          relayDiagnostics,
          options.directTurnTelemetry,
        ),
    },
    options.planning,
  )

  return {
    get state() {
      return sharedBootstrap.state
    },
    get session() {
      return sharedBootstrap.session
    },
    ...(options.directTurnTelemetry ? {directTurnTelemetry: options.directTurnTelemetry} : {}),
    get provisionedIdentity() {
      return sharedBootstrap.provisionedIdentity
    },
    configureRelayDiagnostics: diagnostics => {
      relayDiagnostics = diagnostics
    },
    establish: request => sharedBootstrap.establish(request),
    cancelPendingFirstTurn: () => sharedBootstrap.cancelPendingFirstTurn(),
    reset: () => {
      sharedBootstrap.reset()
      options.directTurnTelemetry?.reset()
    },
    dispose: () => {
      sharedBootstrap.dispose()
      options.directTurnTelemetry?.reset()
    },
  }
}
